import { useState, useEffect, useCallback, useRef } from 'react';
import { blockedByPolicy } from './platform';

/**
 * Anvandarens position, med koll pa att tillstandet kan andras utanfor appen.
 *
 * Den forsta versionen startade en bevakning en gang. Nekades den dog den
 * for gott: gick man till telefonens installningar och slog pa platsen kom
 * appen aldrig underfund med det, utan satt kvar med "nekad" tills sidan
 * laddades om. Har startas bevakningen om nar
 *
 *   - sidan blir synlig igen (typiskt: man har varit i installningarna),
 *   - webblasaren rapporterar att tillstandet andrats,
 *   - eller anvandaren sjalv trycker "Forsok igen".
 *
 * Viktigt: bevakningen far bara startas nar tillstandet redan ar givet.
 * Chrome pa Android tystar platsfragor som inte kommer fran en
 * knapptryckning och svarar PERMISSION_DENIED direkt, utan att spara nagot
 * beslut. En bevakning som startar av sig sjalv vid inloggning blir darfor
 * nekad varje gang, och anvandaren ser ett nej som ingen har sagt. Fragan
 * ska stallas fran en knapp; bevakningen borjar forst efterat.
 */
const OPTIONS = { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 };

/**
 * @param granted true nar webblasaren sager att sidan har tillstand.
 *
 * Skillnaden spelar roll: sager webblasaren ja men positionen anda nekas,
 * sitter sparren i operativsystemet — platstjanster avstangda, eller
 * webblasaren utan platsbehorighet. Att da skylla pa sidan skickar folk
 * till fel installning.
 */
function describe(error, granted) {
  if (!error) return '';
  switch (error.code) {
    case error.PERMISSION_DENIED:
      // Sparren sitter i sidans egna svarshuvuden. Inget pa enheten hjalper.
      if (blockedByPolicy()) {
        return 'Servern förbjuder platsåtkomst (Permissions-Policy). Det måste ändras på servern.';
      }
      return granted
        ? 'Platstjänster är avstängda i systemet, eller så saknar webbläsaren platsbehörighet.'
        : 'Platsåtkomst nekad för den här sidan.';
    case error.POSITION_UNAVAILABLE:
      // Vanligast pa iPhone nar Platstjanster ar avslaget i systemet.
      return 'Enheten kan inte avgöra var du är. Kontrollera att Platstjänster är på.';
    case error.TIMEOUT:
      return 'Det tog för lång tid att hitta din position.';
    default:
      return 'Kunde inte hämta din position.';
  }
}

export default function useUserPosition(enabled) {
  const [position, setPosition] = useState(null);
  const [error, setError] = useState('');
  const [epoch, setEpoch] = useState(0);
  const hasPosition = useRef(false);
  // Vad webblasaren sjalv sager om sidans tillstand, for att kunna skilja
  // ett nej fran sidan fran ett nej fran systemet.
  const granted = useRef(false);

  const retry = useCallback(() => setEpoch(e => e + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;

    if (!navigator.geolocation) {
      setError('Enheten stödjer inte platsbestämning.');
      return undefined;
    }

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        hasPosition.current = true;
        setPosition([pos.coords.latitude, pos.coords.longitude]);
        setError('');
      },
      (err) => setError(describe(err, granted.current)),
      OPTIONS
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [enabled, epoch]);

  // Aterkomst till sidan efter en sväng i telefonens installningar.
  useEffect(() => {
    if (!enabled) return undefined;

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !hasPosition.current) retry();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [enabled, retry]);

  // Webblasaren kan sjalv beratta att tillstandet andrats.
  useEffect(() => {
    if (!enabled || !navigator.permissions || !navigator.permissions.query) return undefined;

    let cancelled = false;
    let status = null;

    navigator.permissions.query({ name: 'geolocation' })
      .then((result) => {
        if (cancelled) return;
        status = result;
        granted.current = result.state === 'granted';
        result.onchange = () => {
          granted.current = result.state === 'granted';
          if (result.state === 'granted') retry();
        };
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
  }, [enabled, retry]);

  return { position, error, retry };
}
